import React, { FC, useState } from 'react';
import {
  List,
  Datagrid,
  TextField,
  FunctionField,
  TopToolbar,
  useListContext,
  useGetOne,
  useNotify,
} from 'react-admin';
import { useNavigate } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBack from '@mui/icons-material/ArrowBack';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { downloadExcel, ExportColumn } from '../utils/exportExcel';

const FAVORITE_EXPORT_COLUMNS: ExportColumn[] = [
  { key: 'favorite_id', label: 'Favorite ID' },
  { key: 'customer_id', label: 'Customer ID' },
  { key: 'company_name', label: 'Company Name' },
  { key: 'customer_name', label: 'Customer Name' },
  { key: 'address_city', label: 'City' },
  { key: 'address_state', label: 'State' },
  { key: 'customer_mobile', label: 'Mobile' },
  { key: 'product_id', label: 'Product ID' },
  { key: 'product_name', label: 'Product Name' },
];

const ProductFavoriteExportButton: FC = () => {
  const { filterValues } = useListContext();
  const productId = filterValues?.product_id;
  const { data: product } = useGetOne('products', { id: productId }, { enabled: !!productId });
  const notify = useNotify();
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const apiBase = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
      const params = new URLSearchParams();
      if (productId) {
        params.set('product_id', String(productId));
      }

      const token = localStorage.getItem('user_authToken');
      const response = await fetch(`${apiBase}/productfavorite/export?${params.toString()}`, {
        headers: {
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (!response.ok) {
        throw new Error('Export failed');
      }

      const payload = await response.json();
      const rows = Array.isArray(payload?.rows) ? payload.rows : [];
      if (!rows.length) {
        notify('No favorites to export', { type: 'info' });
        return;
      }

      const slug = (product?.product_name || `product-${productId || 'all'}`)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');

      downloadExcel({
        filename: `${slug || 'product'}-favorites`,
        sheets: [{ name: 'Favorites', rows, columns: FAVORITE_EXPORT_COLUMNS }],
      });
      notify(`Exported ${rows.length.toLocaleString()} favorite(s)`, { type: 'success' });
    } catch (error) {
      console.error(error);
      notify('Failed to export favorites', { type: 'error' });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Tooltip title="Download Excel">
      <span>
        <IconButton
          aria-label="Download Excel"
          onClick={handleDownload}
          disabled={downloading}
          size="small"
        >
          {downloading ? <CircularProgress size={20} /> : <FileDownloadIcon />}
        </IconButton>
      </span>
    </Tooltip>
  );
};

const CustomToolbar: FC = () => {
  const navigate = useNavigate();
  return (
    <TopToolbar>
      <IconButton onClick={() => navigate('/products')}>
        <ArrowBack />
      </IconButton>
      <ProductFavoriteExportButton />
    </TopToolbar>
  );
};

const ProductFavoriteTitle: FC = () => {
  const { filterValues } = useListContext();
  const productId = filterValues?.product_id;
  const { data, isLoading } = useGetOne('products', { id: productId });
  const name = data?.product_name;
  return <span>{isLoading || !name ? 'Favorites' : `${name} Favorites`}</span>;
};

export const ProductFavoriteList: FC<any> = props => {
  const navigate = useNavigate();

  return (
    <List
      {...props}
      title={<ProductFavoriteTitle />}
      actions={<CustomToolbar />}
    >
      <Datagrid rowClick="edit">
        <FunctionField
          label="Company Name"
          render={(record: { customer_id: number; company_name: string }) => (
            <a
              href="#"
              onClick={e => {
                e.preventDefault();
                navigate(`/customers/${record.customer_id}`);
              }}
            >
              {record.company_name}
            </a>
          )}
        />
        <TextField source="address_city" label="City" />
        <TextField source="address_state" label="State" />
        <TextField source="customer_mobile" label="Mobile" />
      </Datagrid>
    </List>
  );
};

export default ProductFavoriteList;
